/**
 * Contexte de travail SafeCheck Assurance, envoyé à l'API en en-têtes :
 * - X-Insurer-Id : assureur actif (comptes Sinistre et Audit rattachés à plusieurs assureurs) ;
 * - X-Company-Id : entreprise consultée (compte Assurance) ;
 * - X-Hospital-Id : prestataire pour lequel on saisit (compte Sinistre).
 */

const WORK_CONTEXT_KEY = "safecheck-work-context";

export const WORK_CONTEXT_EVENT = "safecheck-work-context-change";

export type WorkContext = {
  insurerId: number | null;
  companyId: number | null;
  hospitalId: number | null;
};

const EMPTY_CONTEXT: WorkContext = { insurerId: null, companyId: null, hospitalId: null };

function toId(value: unknown): number | null {
  const id = Number(value);
  return Number.isInteger(id) && id > 0 ? id : null;
}

export function getWorkContext(): WorkContext {
  if (typeof window === "undefined") return EMPTY_CONTEXT;
  try {
    const raw = localStorage.getItem(WORK_CONTEXT_KEY);
    if (!raw) return EMPTY_CONTEXT;
    const parsed = JSON.parse(raw) as Partial<WorkContext>;
    return {
      insurerId: toId(parsed.insurerId),
      companyId: toId(parsed.companyId),
      hospitalId: toId(parsed.hospitalId),
    };
  } catch {
    return EMPTY_CONTEXT;
  }
}

/** Changer d'assureur vide l'entreprise et le prestataire choisis. */
export function setWorkContext(patch: Partial<WorkContext>): WorkContext {
  const current = getWorkContext();
  const insurerChanged = patch.insurerId !== undefined && patch.insurerId !== current.insurerId;
  const next: WorkContext = {
    insurerId: patch.insurerId !== undefined ? toId(patch.insurerId) : current.insurerId,
    companyId: insurerChanged ? null : patch.companyId !== undefined ? toId(patch.companyId) : current.companyId,
    hospitalId: insurerChanged ? null : patch.hospitalId !== undefined ? toId(patch.hospitalId) : current.hospitalId,
  };
  try {
    localStorage.setItem(WORK_CONTEXT_KEY, JSON.stringify(next));
  } catch {
    // Stockage indisponible : le contexte reste en mémoire pour cet appel seulement.
  }
  window.dispatchEvent(new CustomEvent(WORK_CONTEXT_EVENT, { detail: next }));
  return next;
}

export function clearWorkContext(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(WORK_CONTEXT_KEY);
  } catch {
    // ignore
  }
}

export function workContextHeaders(): Record<string, string> {
  const context = getWorkContext();
  const headers: Record<string, string> = {};
  if (context.insurerId) headers["X-Insurer-Id"] = String(context.insurerId);
  if (context.companyId) headers["X-Company-Id"] = String(context.companyId);
  if (context.hospitalId) headers["X-Hospital-Id"] = String(context.hospitalId);
  return headers;
}
